// pages/ConjugationCompleteExercise.jsx
// Exercice de conjugaison complète - 6 personnes

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dumbbell, Loader2, CheckCircle, XCircle, TrendingUp, RotateCw, BookOpen, List, X, Info } from 'lucide-react';

// Import des utilitaires partagés
import { getLanguageOptions, getLangueFlag, createInsertSpecialCharMultiple } from '../utils/exerciseUtils';
import VirtualKeyboard from '../components/VirtualKeyboard';
import { useLangueEtudiee } from '../hooks/useLangueEtudiee';
import { useLangueInterface } from '../contexts/LangueInterfaceContext';

import { useAuth } from '../contexts/AuthContext';
import './ExerciseShared.css';
import './ConjugationCompleteExercise.css';

export default function ConjugationCompleteExercise() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id || 1; // Fallback à userId=1 pour embed sans auth
  // Paramètres WP passés via l'URL de l'iframe
  const langueEtudiee = useLangueEtudiee();
  const { langueInterface } = useLangueInterface();
  const isEmbed = searchParams.get('embed') === 'true';
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [userAnswers, setUserAnswers] = useState(['', '', '', '', '', '']); // 6 réponses
  const [isChecking, setIsChecking] = useState(false);
  const [validationResults, setValidationResults] = useState(null); // Résultats pour chaque champ
  const [globalResult, setGlobalResult] = useState(null); // Résultat global correct/incorrect
  const [stats, setStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [error, setError] = useState(null);

  // États pour les filtres et modales
  const savedParams = JSON.parse(localStorage.getItem('conjugationCompleteParams') || '{}');
  // langueEtudiee (param WP) prioritaire sur la préférence sauvegardée
  const [languageFilter, setLanguageFilter] = useState(langueEtudiee || savedParams.languageFilter || 'all');
  const [tenseFilter, setTenseFilter] = useState(savedParams.tenseFilter || 'random');
  const [difficultyMode, setDifficultyMode] = useState(savedParams.difficultyMode || 'normal');
  const [verbTypeFilter, setVerbTypeFilter] = useState(savedParams.verbTypeFilter || 'tous');

  const [showTenseExplanation, setShowTenseExplanation] = useState(false);
  const [checkAccents, setCheckAccents] = useState(false);
  const [showRadical, setShowRadical] = useState(false); // Nouvel état pour afficher le radical
  const [showInfinitif, setShowInfinitif] = useState(false); // Afficher l'infinitif dans les champs
  const [showConjugations, setShowConjugations] = useState(false);
  const [currentConjugations, setCurrentConjugations] = useState(null);
  const [loadingConjugations, setLoadingConjugations] = useState(false);

  const [showVerbInfo, setShowVerbInfo] = useState(false);
  const [currentVerbInfo, setCurrentVerbInfo] = useState(null);
  const [loadingVerbInfo, setLoadingVerbInfo] = useState(false);

  const [tenseOptions, setTenseOptions] = useState([{ value: 'random', label: 'Tous les temps', niveau: 0 }]);
  const [userNiveau, setUserNiveau] = useState(0);
  const [currentTenseExplanation, setCurrentTenseExplanation] = useState(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);

  const languageSelectRef = useRef(null);
  const tenseSelectRef = useRef(null);
  const exerciseRef = useRef(null);
  const lastFocusedInputRef = useRef(null); // Mémoriser le dernier input ayant le focus

  const [showExercise, setShowExercise] = useState(false);

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      setShowExercise(false);
      setQuestion(null);
      setUserAnswers(['', '', '', '', '', '']);
      setValidationResults(null);
      setGlobalResult(null);
    }
  }, [location.state]);

  // Charger les temps disponibles depuis l'API
  useEffect(() => {
    if (!userId) return;

    const loadAvailableTenses = async () => {
      try {
        let langueCible = languageFilter;
        if (languageFilter === 'all') {
          langueCible = 'fr';
        }

        const url = `/api/exercices/temps-disponibles?userId=${userId}&langueCible=${langueCible}&mode=${difficultyMode}&langueExplication=${langueInterface}`;

        const response = await fetch(url);
        const data = await response.json();

        if (data.success && data.data && data.data.temps) {
          if (data.warning) {
            console.warn('⚠️', data.warning);
            alert(data.warning);
          }

          setTenseOptions(data.data.temps);
          setUserNiveau(data.data.niveauUtilisateur || 0);

          const tempsDisponibles = data.data.temps.map((t) => t.value);
          if (tenseFilter !== 'random' && !tempsDisponibles.includes(tenseFilter)) {
            setTenseFilter('random');
          }
        } else {
          setTenseOptions([{ value: 'random', label: 'Tous les temps', niveau: 0 }]);
        }
      } catch (error) {
        console.error('❌ Erreur chargement temps disponibles:', error);
        setTenseOptions([{ value: 'random', label: 'Tous les temps', niveau: 0 }]);
      }
    };

    loadAvailableTenses();
  }, [user?.id, languageFilter, difficultyMode, langueInterface]);

  // Fonction pour détectersi verbe irrégulier
  const isVerbIrregular = (verbInfo) => {
    if (!verbInfo || !verbInfo.attributes) return false;
    return verbInfo.attributes.some((attr) => attr.type === 'irregularite' || attr.important === true);
  };

  // Fonction pour générer les options selon la langue
  const getVerbTypeOptions = (langue) => {
    const baseOptions = [{ value: 'tous', label: t('conjugation.verbeTous') }];

    if (!langue) {
      return baseOptions;
    }

    if (langue === 'es') {
      return [
        ...baseOptions,
        { value: 'regulier', label: t('conjugation.verbeRegulier') },
        { value: 'diphtongue_e_ie', label: t('conjugation.verbeDiphtongueEIe') },
        { value: 'diphtongue_o_ue', label: t('conjugation.verbeDiphtongueOUe') },
        { value: 'alternance_e_i', label: t('conjugation.verbeAlternanceEI') },
        { value: 'irregulier_complet', label: t('conjugation.verbeIrregulierComplet') },
      ];
    }

    if (langue === 'fr') {
      return [
        ...baseOptions,
        { value: 'regulier', label: t('conjugation.verbeRegulierFrEr') },
        { value: 'irregulier', label: t('conjugation.verbeIrregulierFr') },
      ];
    }

    if (langue === 'en') {
      return [...baseOptions, { value: 'regulier', label: t('conjugation.verbeRegulierEn') }, { value: 'irregulier', label: t('conjugation.verbeIrregulier') }];
    }

    return [...baseOptions, { value: 'regulier', label: t('conjugation.verbeRegulier') }, { value: 'irregulier', label: t('conjugation.verbeIrregulier') }];
  };

  // Charger une nouvelle question
  const loadNewQuestion = async () => {
    setLoading(true);
    setError(null);
    setValidationResults(null);
    setGlobalResult(null);
    setUserAnswers(['', '', '', '', '', '']);
    setShowRadical(false); // Réinitialiser la checkbox Radical
    setShowInfinitif(false); // Réinitialiser la checkbox Infinitif
    setCurrentVerbInfo(null); // Réinitialiser les infos du verbe
    setLoadingVerbInfo(false); // Réinitialiser le flag de chargement

    try {
      // Ajouter un timestamp pour éviter le cache et forcer un nouveau verbe
      let url = `/api/exercices/conjugaison/complete/random?userId=${userId}&_t=${Date.now()}&langueExplication=${langueInterface}`;

      if (languageFilter !== 'all') {
        url += `&langueCible=${languageFilter}`;
      }

      if (tenseFilter !== 'random') {
        url += `&temps=${tenseFilter}`;
      }

      if (verbTypeFilter !== 'tous') {
        url += `&typeIrregularite=${verbTypeFilter}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Erreur lors du chargement');
      }

      setQuestion(data.data);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour re-tester le même verbe (même question, réinitialiser les réponses)
  const retryCurrentVerb = () => {
    setValidationResults(null);
    setGlobalResult(null);
    setUserAnswers(['', '', '', '', '', '']);
    setShowRadical(false);
    setShowInfinitif(false);

    setTimeout(() => {
      const firstInput = document.querySelector('.answer-input[data-index="0"]');
      if (firstInput) firstInput.focus();
    }, 100);
  };

  // Charger automatiquement les infos du verbe quand la question change
  useEffect(() => {
    if (question && question.verbeId) {
      loadVerbInfoSilent();
    }
  }, [question?.verbeId]); // Dépendance sur question.verbeId pour éviter les re-renders inutiles

  // Focus initial sur le sélecteur de langue
  useEffect(() => {
    if (languageSelectRef.current) {
      languageSelectRef.current.focus();
    }
  }, []);

  // Sauvegarder les paramètres dans localStorage
  useEffect(() => {
    const params = {
      languageFilter,
      tenseFilter,
      difficultyMode,
      verbTypeFilter,
    };
    localStorage.setItem('conjugationCompleteParams', JSON.stringify(params));
  }, [languageFilter, tenseFilter, difficultyMode, verbTypeFilter]);

  // Vérifier la réponse
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier qu'au moins un champ est rempli
    if (userAnswers.every((ans) => !ans.trim())) return;

    setIsChecking(true);

    try {
      const response = await fetch('/api/exercices/conjugaison/complete/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verbeId: question.verbeId,
          temps: question.temps,
          mode: question.mode,
          reponsesUtilisateur: userAnswers,
          reponsesCorrectes: question.reponsesCorrectes,
          userId: userId,
          checkAccents: checkAccents,
        }),
      });

      if (response.status === 429) {
        const data = await response.json();
        setError(`Limite quotidienne atteinte (${data.limite} exercices/jour). Passez à un abonnement supérieur pour continuer.`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setValidationResults(data.details); // Détails pour chaque champ
        setGlobalResult(data.correct); // true/false global

        setStats((prev) => ({
          correct: prev.correct + (data.correct ? 1 : 0),
          incorrect: prev.incorrect + (data.correct ? 0 : 1),
          total: prev.total + 1,
        }));
      }
    } catch (err) {
      console.error('Erreur vérification:', err);
      setError('Erreur lors de la vérification');
    } finally {
      setIsChecking(false);
    }
  };

  // Fonction pour insérer un caractère spécial - utilise la factory pour inputs multiples
  const insertSpecialChar = createInsertSpecialCharMultiple(lastFocusedInputRef, userAnswers, setUserAnswers);

  // Fonction pour extraire le radical du verbe
  const getVerbRadical = (verbInfo = currentVerbInfo) => {
    if (!verbInfo || !verbInfo.attributes) return null;
    
    // Vérifier que verbInfo correspond bien à la question actuelle
    if (question && verbInfo.verbeId !== question.verbeId) return null;
    
    // Chercher l'attribut qui contient le radical
    const radicalAttr = verbInfo.attributes.find(
      attr => attr.label && (attr.label.toLowerCase().includes('radical') || attr.label.toLowerCase().includes('racine'))
    );
    
    return radicalAttr ? radicalAttr.value : null;
  };

  // Fonction pour gérer le toggle de la checkbox Radical
  const handleRadicalToggle = (checked) => {
    setShowRadical(checked);
    // Désactiver l'infinitif si on active le radical
    if (checked) setShowInfinitif(false);

    // Extraire le radical directement sans vérifier la correspondance des IDs
    if (!currentVerbInfo || !currentVerbInfo.attributes) {
      return;
    }
    
    const radicalAttr = currentVerbInfo.attributes.find(
      attr => attr.label && (attr.label.toLowerCase().includes('radical') || attr.label.toLowerCase().includes('racine'))
    );
    
    const radical = radicalAttr ? radicalAttr.value : null;
    
    if (!radical) return;
    
    const newAnswers = [...userAnswers];
    
    if (checked) {
      // Ajouter le radical aux champs vides
      for (let i = 0; i < newAnswers.length; i++) {
        if (newAnswers[i].trim() === '') {
          newAnswers[i] = radical;
        }
      }
    } else {
      // Retirer le radical seulement si le champ contient uniquement le radical
      for (let i = 0; i < newAnswers.length; i++) {
        if (newAnswers[i] === radical) {
          newAnswers[i] = '';
        }
      }
    }
    
    setUserAnswers(newAnswers);
    
    // Focus sur le premier champ après activation
    if (checked) {
      setTimeout(() => {
        const firstInput = document.querySelector('.answer-input[data-index="0"]');
        if (firstInput) {
          firstInput.focus();
          // Positionner le curseur à la fin du radical
          firstInput.setSelectionRange(radical.length, radical.length);
        }
      }, 100);
    }
  };

  // Fonction pour gérer le toggle de la checkbox Infinitif
  const handleInfinitifToggle = (checked) => {
    setShowInfinitif(checked);
    if (!question) return;

    const infinitif = question.infinitifBase;
    if (!infinitif) return;

    const newAnswers = [...userAnswers];

    if (checked) {
      // Désactiver le radical si actif
      if (showRadical) setShowRadical(false);
      // Ajouter l'infinitif aux champs vides
      for (let i = 0; i < newAnswers.length; i++) {
        if (newAnswers[i].trim() === '') {
          newAnswers[i] = infinitif;
        }
      }
    } else {
      // Retirer l'infinitif seulement si le champ contient uniquement l'infinitif
      for (let i = 0; i < newAnswers.length; i++) {
        if (newAnswers[i] === infinitif) {
          newAnswers[i] = '';
        }
      }
    }

    setUserAnswers(newAnswers);

    if (checked) {
      setTimeout(() => {
        const firstInput = document.querySelector('.answer-input[data-index="0"]');
        if (firstInput) {
          firstInput.focus();
          firstInput.setSelectionRange(infinitif.length, infinitif.length);
        }
      }, 100);
    }
  };

  // Fonction pour démarrer l'exercice et scroller
  const startExercise = () => {
    setShowExercise(true);
    // Toujours charger une nouvelle question au démarrage
    loadNewQuestion();
    setTimeout(() => {
      exerciseRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  };

  // Fonction pour revenir aux paramètres
  const backToSettings = () => {
    setShowExercise(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculer le pourcentage de réussite
  const successRate = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  // Fonction pour charger l'explication du temps actuel
  const loadTenseExplanation = async () => {
    if (loadingExplanation || !question) return;

    setLoadingExplanation(true);

    try {
      const response = await fetch(`/api/exercices/explication-temps?mode=${question.mode}&temps=${question.temps}&langueCible=${question.langueCible}&langueExplication=${langueInterface}`);

      if (!response.ok) {
        throw new Error('Erreur API');
      }

      const data = await response.json();

      if (data.success) {
        setCurrentTenseExplanation(data.data);
        setShowTenseExplanation(true);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
    } finally {
      setLoadingExplanation(false);
    }
  };

  // Fonction pour charger les conjugaisons du verbe actuel
  const loadConjugations = async () => {
    if (loadingConjugations || !question) return;

    setLoadingConjugations(true);

    try {
      const response = await fetch(
        `/api/exercices/conjugaisons-verbe?verbeId=${question.verbeId}&langue=${question.langueCible}&mode=${question.mode}&temps=${question.temps}`
      );

      if (!response.ok) throw new Error('Erreur API');

      const data = await response.json();

      if (data.success) {
        setCurrentConjugations(data);
        setShowConjugations(true);
      }
    } catch (error) {
      console.error('❌ Erreur conjugaisons:', error);
    } finally {
      setLoadingConjugations(false);
    }
  };

  // Charger les infos du verbe (sans ouvrir la modale)
  const loadVerbInfoSilent = async () => {
    if (loadingVerbInfo || !question) {
      return;
    }
    
    setLoadingVerbInfo(true);

    try {
      const response = await fetch(`/api/exercices/info-verbe?verbId=${question.verbeId}`);
      if (!response.ok) throw new Error('Erreur API');
      const data = await response.json();

      if (data.success) {
        // Ajouter l'ID du verbe aux infos pour pouvoir vérifier la correspondance
        setCurrentVerbInfo({ ...data.data, verbeId: question.verbeId });
      }
    } catch (error) {
      console.error('❌ Erreur infos verbe:', error);
    } finally {
      setLoadingVerbInfo(false);
    }
  };

  // Charger ET afficher la modale
  const loadVerbInfo = async () => {
    await loadVerbInfoSilent();
    setShowVerbInfo(true);
  };

  // Calculer le score partiel (nombre de réponses correctes sur 6)
  const getPartialScore = () => {
    if (!validationResults) return null;
    const correctCount = validationResults.filter((r) => r.correct).length;
    return `${correctCount}/6`;
  };

  return (
    <div className="conjugation-complete-exercise">
      {/* ÉCRAN 1 : PARAMÈTRES */}
      {!showExercise && (
        <div className="settings-screen">
          {!isEmbed && (
            <div className="exercise-header">
              <div className="header-title">
                <Dumbbell size={32} color="#4F46E5" />
                <h2>{t('conjugationComplete.title')}</h2>
              </div>
            </div>
          )}

          <div className="settings-container">
            <h3 style={{ textAlign: 'center', marginBottom: '24px' }}>{t('conjugation.chooseParams')}</h3>

            {/* Filtre de langue */}
            <div className="setting-item">
              <label>{t('conjugation.langueExercice')}</label>
              <select ref={languageSelectRef} value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)} className="language-select">
                {getLanguageOptions(t).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.flag} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur de mode de difficulté */}
            <div className="setting-item">
              <label>{t('conjugation.modeDifficulte')}</label>
              <select value={difficultyMode} onChange={(e) => setDifficultyMode(e.target.value)} className="language-select">
                <option value="strict">{t('conjugation.modeStrict')}</option>
                <option value="normal">{t('conjugation.modeNormal')}</option>
                <option value="challenge">{t('conjugation.modeChallenge')}</option>
              </select>
            </div>

            {/* Filtre de temps */}
            <div className="setting-item">
              <label>{t('conjugation.tempsATravailler')}</label>
              <select ref={tenseSelectRef} value={tenseFilter} onChange={(e) => setTenseFilter(e.target.value)} className="language-select">
                {tenseOptions.map((option, index) => (
                  <option key={option.value === 'random' ? 'random' : `${option.langue}-${option.mode}-${option.value}-${index}`} value={option.value}>
                    {option.value === 'random' ? `⏰ ${option.label}` : `⏰ ${option.label} [Niv. ${option.niveau}]`}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur de type de verbe */}
            <div className="setting-item">
              <label>{t('conjugation.typeVerbe')}</label>
              <select value={verbTypeFilter} onChange={(e) => setVerbTypeFilter(e.target.value)} className="language-select">
                {(getVerbTypeOptions(languageFilter) || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Badge niveau utilisateur */}
            {userNiveau >= 0 && (
              <div className="setting-item" style={{ textAlign: 'center' }}>
                <div className="user-level-badge" style={{ display: 'inline-block' }}>
                  {getLangueFlag(languageFilter === 'all' ? 'fr' : languageFilter)} {t('conjugation.votreNiveau')} : {userNiveau}
                </div>
              </div>
            )}

            {/* Bouton Commencer */}
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button onClick={startExercise} className="btn-start-exercise">
                <Dumbbell size={24} />
                {t('conjugation.start')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ÉCRAN 2 : EXERCICE */}
      {showExercise && (
        <>
          <div ref={exerciseRef}></div>

          {showExercise && (
            <>
              {/* Message d'erreur */}
              {error && (
                <div className="exercise-error">
                  <XCircle size={20} />
                  <div>
                    <p className="error-title">{t('conjugation.erreur')}</p>
                    <p className="error-message">{error}</p>
                    {error.includes('Aucun verbe') && <p className="error-hint">{t('conjugation.erreurHintEnrichissement')}</p>}
                  </div>
                </div>
              )}

              {/* Chargement */}
              {loading && !error && (
                <div className="exercise-loading">
                  <Loader2 size={48} className="spinning" color="#4F46E5" />
                  <p>{t('conjugation.chargementQuestion')}</p>
                </div>
              )}

              {/* Question */}
              {!loading && !error && question && (
                <div className="exercise-card">
                  {/* Boutons d'aide */}
                  <div className="help-buttons">
                    <button onClick={loadTenseExplanation} className="btn-explanation" disabled={!question || loadingExplanation} title={t('conjugation.explicationTemps')}>
                      {loadingExplanation ? (
                        <>
                          <Loader2 size={20} className="spinning" />
                          {t('conjugation.chargement')}
                        </>
                      ) : (
                        <>
                          <BookOpen size={20} />
                          {t('conjugation.explicationTemps')}
                        </>
                      )}
                    </button>

                    <button onClick={loadConjugations} className="btn-explanation" disabled={!question || loadingConjugations} title={t('conjugation.voirConjugaisons')}>
                      {loadingConjugations ? (
                        <>
                          <Loader2 size={16} className="spinning" />
                          {t('conjugation.chargement')}
                        </>
                      ) : (
                        <>
                          <List size={16} />
                          {t('conjugation.voirConjugaisons')}
                        </>
                      )}
                    </button>

                    <button
                      onClick={loadVerbInfo}
                      className={`btn-explanation ${currentVerbInfo && isVerbIrregular(currentVerbInfo) ? 'irregular' : ''}`}
                      disabled={!question || loadingVerbInfo}
                      title={t('conjugation.aProposVerbe')}
                    >
                      {loadingVerbInfo ? (
                        <>
                          <Loader2 size={16} className="spinning" />
                          {t('conjugation.chargement')}
                        </>
                      ) : (
                        <>
                          <Info size={16} />{t('conjugation.aProposVerbe')}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Consigne compacte */}
                  <div className="question-compact">
                    <div className="verb-info-block">
                      <div className="verb-line">
                        <span className="verb-flag">{question.langueCible === 'fr' ? '🇫🇷' : question.langueCible === 'en' ? '🇬🇧' : '🇪🇸'}</span>
                        <span className="verb-target">{question.infinitifCible.toUpperCase()}</span>
                        {question.infinitifFr && <span className="verb-fr-hint"> ({question.infinitifFr})</span>}
                      </div>
                      <div className="info-line">
                        <span className="info-label">{t('conjugation.temps')}:</span>
                        <span className="info-value">{question.tempsLabel}</span>
                      </div>
                      <div className="info-line">
                        <span className="info-label">{t('conjugationComplete.conjuguerToutesPersonnes')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Formulaire de réponse avec 6 champs */}
                  {!globalResult && (
                    <form onSubmit={handleSubmit} className="answer-form">
                      {/* Case à cocher et clavier virtuel selon la langue */}
                      {(question.langueCible === 'es' || question.langueCible === 'fr') && (
                        <div className="language-helpers">
                          <label className="checkbox-label">
                            <input 
                              type="checkbox" 
                              checked={checkAccents} 
                              onChange={(e) => {
                                setCheckAccents(e.target.checked);
                                // Si on active la vérification des accents, focus sur le premier champ
                                if (e.target.checked) {
                                  setTimeout(() => {
                                    const firstInput = document.querySelector('.answer-input[data-index="0"]');
                                    if (firstInput) {
                                      firstInput.focus();
                                    }
                                  }, 100);
                                }
                              }} 
                            />
                            {t('conjugationComplete.verifierAccents')}
                          </label>

                          <label className="checkbox-label">
                            <input 
                              type="checkbox" 
                              checked={showRadical} 
                              onChange={(e) => handleRadicalToggle(e.target.checked)}
                              disabled={
                                !currentVerbInfo || 
                                !currentVerbInfo.attributes || 
                                !question ||
                                currentVerbInfo.verbeId !== question.verbeId ||
                                !currentVerbInfo.attributes.some(attr => 
                                  attr.label && (attr.label.toLowerCase().includes('radical') || attr.label.toLowerCase().includes('racine'))
                                )
                              }
                            />
                            {t('conjugationComplete.afficherRadical')}
                          </label>

                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={showInfinitif}
                              onChange={(e) => handleInfinitifToggle(e.target.checked)}
                              disabled={!question || !question.infinitifBase}
                            />
                            {t('conjugationComplete.afficherInfinitif')}
                          </label>

                          {checkAccents && (
                            <VirtualKeyboard
                              langue={question.langueCible}
                              checkAccents={true}
                              onCheckAccentsChange={() => {}}
                              onCharClick={insertSpecialChar}
                              showCheckbox={false}
                            />
                          )}
                        </div>
                      )}

                      {/* Grille des 6 champs de saisie */}
                      <div className="conjugation-grid">
                        {question.pronouns.map((pronoun, index) => (
                          <div key={index} className="answer-input-group">
                            <span className="pronoun">{pronoun}</span>
                            <input
                              type="text"
                              data-index={index}
                              value={
                                validationResults && validationResults[index] && !validationResults[index].correct
                                  ? `${userAnswers[index]} → ${validationResults[index].correctAnswer}`
                                  : userAnswers[index]
                              }
                              onChange={(e) => {
                                const newAnswers = [...userAnswers];
                                newAnswers[index] = e.target.value;
                                setUserAnswers(newAnswers);
                              }}
                              onFocus={(e) => {
                                lastFocusedInputRef.current = e.target;
                              }}
                              placeholder="..."
                              className={`answer-input ${
                                validationResults && validationResults[index]
                                  ? validationResults[index].correct
                                    ? 'correct'
                                    : 'incorrect'
                                  : ''
                              }`}
                              disabled={isChecking || globalResult !== null}
                              autoCapitalize="off"
                              autoCorrect="off"
                              spellCheck="false"
                              autoFocus={index === 0 && window.innerWidth > 768}
                            />
                          </div>
                        ))}
                      </div>

                      {globalResult === null && (
                        <button type="submit" className="btn-verify" disabled={userAnswers.every((ans) => !ans.trim()) || isChecking}>
                          {isChecking ? (
                            <>
                              <Loader2 size={20} className="spinning" />
                              {t('conjugation.verification')}
                            </>
                          ) : (
                            <>
                              <CheckCircle size={20} />
                              {t('conjugationComplete.verifierMesReponses')}
                            </>
                          )}
                        </button>
                      )}

                      {/* Bouton passer à un autre verbe */}
                      {(globalResult === null || globalResult === true) && (
                        <button type="button" onClick={loadNewQuestion} className="btn-verify">
                          <RotateCw size={20} />
                          {t('conjugation.passerAutreVerbe')}
                        </button>
                      )}
                    </form>
                  )}

                  {/* Résultat global */}
                  {globalResult !== null && (
                    <>
                      <div className={`result-box ${globalResult ? 'correct' : 'incorrect'}`}>
                        <div className="result-icon">{globalResult ? <CheckCircle size={48} color="#10B981" /> : <XCircle size={48} color="#EF4444" />}</div>
                        <div className="result-content">
                          <h3 className="result-title">{globalResult ? t('conjugationComplete.parfait') : t('conjugationComplete.presque')}</h3>
                          <p className="result-message">
                            {t('conjugationComplete.scoreBonnesReponses', { score: getPartialScore() })}
                          </p>
                        </div>
                      </div>

                      {/* Stats compactes */}
                      {stats.total > 0 && (
                        <div className="stats-compact">
                          <span className="stat-compact">
                            <CheckCircle size={16} color="#10B981" />
                            {stats.correct}
                          </span>
                          <span className="stat-compact">
                            <XCircle size={16} color="#EF4444" />
                            {stats.incorrect}
                          </span>
                          <span className="stat-compact">
                            <TrendingUp size={16} color="#4F46E5" />
                            {successRate}%
                          </span>
                        </div>
                      )}

                      <div className="result-actions">
                        {!globalResult && (
                          <button onClick={retryCurrentVerb} className="btn-retry">
                            <RotateCw size={20} />
                            {t('conjugation.retesterVerbe')}
                          </button>
                        )}
                        <button onClick={loadNewQuestion} className="btn-next">
                          <RotateCw size={20} />
                          {t('conjugation.passerAutreVerbe')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {showExercise && (
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                  <button onClick={backToSettings} className="btn-back-settings">
                    {t('conjugation.modifierParametres')}
                  </button>
                </div>
              )}
            </>
          )}

          {/* MODALE : Explication du temps */}
          {showTenseExplanation && currentTenseExplanation && (
            <div className="modal-overlay" onClick={() => setShowTenseExplanation(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <BookOpen size={24} color="#4F46E5" />
                    {currentTenseExplanation.title}
                  </h3>
                  <button onClick={() => setShowTenseExplanation(false)} className="modal-close">
                    <X size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  <div className="explanation-section">
                    <h4>{t('conjugation.descriptionSection')}</h4>
                    <p className="modal-description">{currentTenseExplanation.description}</p>
                  </div>

                  {currentTenseExplanation.descriptifMode && (
                    <div className="explanation-section">
                      <h4>{t('conjugation.modeSection')}</h4>
                      <p className="modal-description">{currentTenseExplanation.descriptifMode}</p>
                    </div>
                  )}

                  {currentTenseExplanation.descriptifTemps && (
                    <div className="explanation-section">
                      <h4>{t('conjugation.tempsSection')}</h4>
                      <p className="modal-description">{currentTenseExplanation.descriptifTemps}</p>
                    </div>
                  )}

                  <div className="explanation-section">
                    <h4>{t('conjugation.utilisationSection')}</h4>
                    <p className="modal-usage">{currentTenseExplanation.usage}</p>
                  </div>

                  {currentTenseExplanation.examples && currentTenseExplanation.examples.length > 0 && (
                    <div className="explanation-section">
                      <h4>{t('conjugation.exemplesSection')}</h4>
                      <ul className="modal-examples">
                        {currentTenseExplanation.examples.map((example, index) => (
                          <li key={index}>{example}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* MODALE : Conjugaisons complètes */}
          {showConjugations && currentConjugations && (
            <div className="modal-overlay" onClick={() => setShowConjugations(false)}>
              <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <List size={24} color="#4F46E5" />
                    {t('conjugation.conjugaisons')} : {currentConjugations.infinitif}
                  </h3>
                  <button onClick={() => setShowConjugations(false)} className="modal-close">
                    <X size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  <div className="conjugation-info">
                    <p>
                      <strong>{t('conjugation.mode')} :</strong> {currentConjugations.mode}
                    </p>
                    <p>
                      <strong>{t('conjugation.temps')} :</strong> {currentConjugations.temps}
                    </p>
                  </div>

                  {currentConjugations.note && <div className="conjugation-note">ℹ️ {currentConjugations.note}</div>}

                  <div className="conjugation-table">
                    {currentConjugations.conjugaisons.map((item, index) => (
                      <div key={index} className={`conjugation-row ${!item.forme || item.forme === '-' ? 'empty' : ''}`}>
                        <span className="pronoun-col">{item.pronoun}</span>
                        <span className="form-col">{item.forme && item.forme !== '-' ? item.forme : t('conjugation.nonUtilise')}</span>
                      </div>
                    ))}
                  </div>

                  <div className="conjugation-tip">
                    {t('conjugation.astuce')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MODALE : Informations sur le verbe */}
          {showVerbInfo && currentVerbInfo && (
            <div className="modal-overlay" onClick={() => setShowVerbInfo(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <Info size={24} color="#4F46E5" />{t('conjugation.aProposDe')} : {currentVerbInfo.infinitif}
                  </h3>
                  <button onClick={() => setShowVerbInfo(false)} className="modal-close">
                    <X size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  {currentVerbInfo.attributes.length > 0 ? (
                    <div className="verb-attributes">
                      {currentVerbInfo.attributes.map((attr, index) => (
                        <div key={index} className={`attribute-item ${attr.important ? 'important' : ''}`}>
                          <div className="attribute-icon">{attr.icon}</div>
                          <div className="attribute-content">
                            <div className="attribute-label">{attr.label}</div>
                            <div className="attribute-value">{attr.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-attributes">{t('conjugation.pasDeCaracteristiques')}</p>
                  )}

                  <div className="verb-info-tip">
                    {t('conjugation.bonASavoir')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
